import React from "react";
import type { BaseProps } from "@purpur/common-types";
import c from "classnames/bind";

import styles from "./icon.module.scss";
import type { iconList } from "./icon-imports";

export declare type IconName = keyof typeof iconList;

const cx = c.bind(styles);
const rootClassName = "purpur-icon";

export const sizes = ["xxs", "xs", "sm", "md", "lg", "xl"] as const;
export const DEFAULT_SIZE = "md" as const;

export type Size = (typeof sizes)[number];

type CreateSvg = {
  content?: string;
  title?: string;
  ariaHidden?: boolean;
};

export type IconBaseProps = Omit<BaseProps<"span">, "children"> & {
  size?: Size;
  allyTitle?: string;
};

export type IconProps = {
  svg: { svg: string; name: string };
} & IconBaseProps;

// Create string from array of attributes
const getAttributesString = (attributeCollection: Record<string, unknown>[]) =>
  attributeCollection
    .filter((attr) => Object.keys(attr).length >= 1) // remove empty object
    .map((item) => `${item.name}="${item.value}"`)
    .join(" ");

const createSvg = ({ content = "", title }: CreateSvg = {}) => {
  const attributeCollection = [
    { name: "xmlns", value: "http://www.w3.org/2000/svg" },
    { name: "fill", value: "currentColor" },
    { name: "viewBox", value: "0 0 24 24" },
    title ? { name: "role", value: "img" } : { name: "aria-hidden", value: "true" },
  ];

  const titleElement = title ? `<title>${title}</title>` : "";
  return `<svg ${getAttributesString(attributeCollection)}>${titleElement}${content}</svg>`;
};

// Remove svg tags
const getSvgContent = (svg: string): string => svg.replace(/<(\/?)svg([^>]*)>/g, "").trim();

export const Icon = ({ svg, allyTitle, className, size = DEFAULT_SIZE, ...props }: IconProps) => {
  const svgString = createSvg({
    content: getSvgContent(svg.svg),
    title: allyTitle,
  });

  const classes = cx(rootClassName, `${rootClassName}--${size}`, className);

  return <span className={classes} dangerouslySetInnerHTML={{ __html: svgString }} {...props} />;
};

Icon.displayName = "Icon";
